import React from 'react';
import { X, Check, Sparkles } from 'lucide-react';
import Button from './Button';
import { Profile } from '../lib/supabase';
import { getAvailableUpgradeIds, getLicenseSummary } from '../utils/licensing';

interface LicenseUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: Profile | null;
}

const licenses = [
  {
    id: 'free',
    name: 'Free License',
    emoji: '🆓',
    price: '$0',
    credits: 10,
    bestFor: 'New users wanting to test the platform\'s core functionality.',
    features: [
      'Access to ~20 basic templates',
      'Standard resolution downloads',
      'Watermark on all generated images',
      'Personal use only'
    ],
    limitations: ['No commercial use', 'Watermarked images', 'Limited templates']
  },
  {
    id: 'commercial',
    name: 'Commercial License',
    emoji: '💼',
    price: '$37',
    credits: 275,
    bestFor: 'Solopreneurs, freelancers, and small businesses who need mockups for their own brand\'s marketing.',
    features: [
      'Full Commercial Use Rights',
      'No watermarks',
      'High-resolution downloads',
      'Access to 100+ standard templates',
      '1 Brand Kit'
    ],
    limitations: []
  },
  {
    id: 'template_pack',
    name: 'Template Pack License',
    emoji: '🎨',
    price: '$67',
    credits: 500,
    bestFor: 'Digital creators and designers who sell their own templates (on Etsy, Creative Market, etc.)',
    features: [
      'All Commercial License features',
      'Special Resale License for template displays',
      'Use mockups to market digital products',
      'Perfect for Etsy & Creative Market sellers'
    ],
    limitations: []
  },
  {
    id: 'professional',
    name: 'Professional License',
    emoji: '🚀',
    price: '$97',
    credits: 725,
    bestFor: 'Power users, professional marketers, and designers who want the full creative power of AI.',
    features: [
      'All Commercial License features',
      'Dynamic Scene Generation (text prompts)',
      'AI-Powered Copywriting',
      'Style Modifiers control',
      '5 Brand Kits',
      'Early access to beta features'
    ],
    limitations: []
  },
  {
    id: 'agency',
    name: 'Agency License',
    emoji: '🏢',
    price: '$147',
    credits: 1100,
    bestFor: 'Design studios, marketing agencies, and larger teams that need collaboration and high-volume output.',
    features: [
      'All Professional License features',
      '5 User Seats included',
      'Shared Brand Kits & Folders',
      'Campaign Mode (Batch Generation)',
      'AI Recomposition',
      'Priority support'
    ],
    limitations: []
  }
];

const LicenseUpgradeModal: React.FC<LicenseUpgradeModalProps> = ({ isOpen, onClose, profile }) => {
  if (!isOpen) return null;

  const availableUpgradeIds = getAvailableUpgradeIds(profile);
  const availableUpgrades = licenses.filter((license) =>
    availableUpgradeIds.includes(license.id as 'commercial' | 'template_pack' | 'professional' | 'agency'),
  );
  const licenseSummary = getLicenseSummary(profile);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-text-dark">Upgrade Your License</h2>
            <p className="text-gray-600 mt-1">Current access: {licenseSummary}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto">
          <div className="p-6">
            {availableUpgrades.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-brand-purple mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-dark mb-2">
                  You already own every in-app upgrade we currently expose here.
                </h3>
                <p className="text-gray-600">
                  Agency, Professional, Commercial, and Template Pack are already covered on this account.
                </p>
              </div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
                {availableUpgrades.map((license) => (
                  <div
                    key={license.id}
                    className={`border-2 rounded-xl p-6 hover:border-brand-purple transition-colors ${
                      license.id === 'agency' ? 'border-purple-200 bg-purple-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-text-dark flex items-center gap-2">
                          <span className="text-2xl">{license.emoji}</span>
                          {license.name}
                        </h3>
                        <p className="text-3xl font-bold text-brand-purple mt-2">{license.price}</p>
                        <p className="text-sm text-gray-600 mt-1">{license.credits.toLocaleString()} Credits</p>
                      </div>
                      {license.id === 'agency' && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                          Best Value
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-700 mb-4 italic">
                      Best for: {license.bestFor}
                    </p>

                    <div className="space-y-2 mb-6">
                      {license.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => {
                        // TODO: Implement Stripe checkout
                        window.open(`/checkout?license=${license.id}`, '_blank');
                      }}
                    >
                      Upgrade to {license.name.replace(' License', '')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseUpgradeModal;
